import React from 'react';
import { useLang } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Globe, ArrowUpRight, Zap } from 'lucide-react';

const ORDER_URL = 'https://www.host-on.de/zimbra#pakete';

export default function Footer() {
  const { t, lang, toggle } = useLang();

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer data-testid="footer-section" className="bg-[#050505] relative overflow-hidden">
      {/* CTA Banner */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-neon/10 via-pink/10 to-cyan/10" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-6">
              {lang === 'de' ? 'Bereit für professionelle E-Mail?' : 'Ready for professional email?'}
            </h2>
            <p className="text-lg text-zinc-400 font-light mb-10 max-w-xl mx-auto">
              {lang === 'de'
                ? 'Starte jetzt mit Zimbra und erlebe E-Mail auf einem neuen Level.'
                : 'Start with Zimbra now and experience email on a new level.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 bg-neon text-black font-bold text-sm uppercase tracking-wide rounded-full px-10 py-5 hover:bg-white hover:shadow-[0_0_40px_rgba(212,255,0,0.6)] transition-all duration-300 glow-neon"
                data-testid="cta-footer-order"
              >
                <Zap className="w-5 h-5" />
                {lang === 'de' ? 'Jetzt bestellen' : 'Order now'}
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
              <button
                onClick={() => scrollTo('#enterprise')}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/20 text-white font-bold text-sm uppercase tracking-wide rounded-full px-10 py-5 hover:border-pink hover:text-pink transition-all duration-300"
                data-testid="cta-footer-enterprise"
              >
                {lang === 'de' ? 'Enterprise-Anfrage' : 'Enterprise inquiry'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Zimbra branding + Massive Typography */}
      <div className="max-w-full px-6 lg:px-8 py-8 overflow-hidden border-t border-white/[0.04]">
        <div className="flex items-center justify-center gap-4 mb-4">
          <img src="/images/zimbra-logo.svg" alt="Zimbra" className="w-10 h-10" />
          <span className="text-sm text-zinc-500 font-medium">Powered by Zimbra Collaboration Suite</span>
        </div>
        <div className="text-[14vw] sm:text-[12vw] lg:text-[9vw] font-heading font-black tracking-tighter text-transparent leading-none select-none whitespace-nowrap text-center" style={{ WebkitTextStroke: '1px rgba(255,255,255,0.04)' }}>
          ZIMBRA.JETZT
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/[0.04] pt-8">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-2 text-sm text-zinc-600">
            <span>{t('footer.powered')} <a href="https://www.host-on.de" target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-neon transition-colors font-medium">Host-On.de</a></span>
            <span className="text-zinc-800">|</span>
            <a href="/datenschutz" className="hover:text-zinc-400 transition-colors">{t('footer.privacy')}</a>
            <a href="/impressum" className="hover:text-zinc-400 transition-colors">{t('footer.imprint')}</a>
          </div>

          <button
            onClick={toggle}
            className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-neon transition-colors px-3 py-1.5 rounded-full border border-white/[0.06] hover:border-neon/30"
            data-testid="footer-lang-toggle"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === 'de' ? 'English' : 'Deutsch'}
          </button>
        </div>

        <p className="text-center text-xs text-zinc-800 mt-6">
          &copy; {new Date().getFullYear()} zimbra.jetzt &middot; {t('footer.rights')}
        </p>
      </div>
    </footer>
  );
}
